import React, { useState, useEffect, useRef } from "react";
import { API } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Settings, Loader2, Upload, ImageIcon, LifeBuoy } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "platform_name", label: "Platform name", placeholder: "Site Pro App" },
  { key: "support_email", label: "Support email (shown to customers)", placeholder: "chris@siteproapp.au", type: "email" },
  { key: "support_phone", label: "Support phone (owners only)", placeholder: "0410 344 600" },
  { key: "support_hours", label: "Support hours", placeholder: "Mon–Fri, 7am–5pm AEST" },
  { key: "callback_sla", label: "Callback promise", placeholder: "24 hours" },
];

export default function PlatformBranding() {
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [form, setForm] = useState({
    platform_name: "", support_email: "", support_phone: "", support_hours: "", callback_sla: "",
  });

  const token = () => localStorage.getItem("token");

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/platform/settings`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) throw new Error("load failed");
      const a = await res.json();
      setForm({
        platform_name: a.platform_name || "", support_email: a.support_email || "",
        support_phone: a.support_phone || "", support_hours: a.support_hours || "",
        callback_sla: a.callback_sla || "",
      });
      setLogoUrl(a.logo_url ? `${API.replace(/\/api$/, "")}${a.logo_url}?v=${Date.now()}` : null);
    } catch (e) {
      toast.error("Could not load platform settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/platform/settings`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token()}`, "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Save failed"); }
      toast.success("Platform branding saved");
    } catch (e) {
      toast.error(e.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const onLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be 2MB or smaller"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/platform/logo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token()}` },
        body: fd,
      });
      if (!res.ok) { const er = await res.json(); throw new Error(er.detail || "Upload failed"); }
      const data = await res.json();
      setLogoUrl(`${API.replace(/\/api$/, "")}${data.logo_url}?v=${Date.now()}`);
      toast.success("Platform logo updated");
    } catch (er) {
      toast.error(er.message || "Could not upload logo");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="platform-branding-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-xl bg-slate-900 flex items-center justify-center shadow-sm">
          <Settings className="w-6 h-6 text-amber-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Platform Branding</h1>
          <p className="text-slate-500 text-sm">Operator-only. Powers the login &amp; pricing pages, email footer and customer support contact.</p>
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm mt-6">
        <CardContent className="p-6">
          <div className="grid md:grid-cols-[200px_1fr] gap-8">
            <div>
              <Label className="text-sm font-medium text-slate-700">Platform logo</Label>
              <div className="mt-2 w-44 h-44 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                {logoUrl ? (
                  <img src={logoUrl} alt="Platform logo" className="w-full h-full object-contain p-2" data-testid="platform-logo-preview" />
                ) : (
                  <div className="text-center text-slate-400">
                    <ImageIcon className="w-8 h-8 mx-auto" />
                    <p className="text-xs mt-1">No logo yet</p>
                  </div>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onLogoPick} data-testid="platform-logo-input" />
              <Button variant="outline" size="sm" className="mt-3 w-44 border-slate-300" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-platform-logo-btn">
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {logoUrl ? "Replace logo" : "Upload logo"}
              </Button>
              <p className="text-xs text-slate-400 mt-2 w-44">PNG, JPG or WEBP · max 2MB</p>
            </div>

            <div className="space-y-4">
              {FIELDS.map((f) => (
                <div key={f.key}>
                  <Label htmlFor={f.key} className="text-sm font-medium text-slate-700">{f.label}</Label>
                  <Input
                    id={f.key}
                    type={f.type || "text"}
                    value={form[f.key]}
                    placeholder={f.placeholder}
                    onChange={(e) => onChange(f.key, e.target.value)}
                    className="mt-1 border-slate-200 focus:border-amber-400"
                    data-testid={`platform-${f.key}-input`}
                  />
                </div>
              ))}
              <div className="flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-500">
                <LifeBuoy className="w-4 h-4 mt-0.5 shrink-0" />
                <span>The email <strong>sender</strong> address (noreply@siteproapp.au) is set in server config and stays Resend-verified. The <strong>support email</strong> above is what customers see and reply to. Downstream users (site managers, view-only) only see an email-us prompt with the callback promise — never the phone number.</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-slate-100">
            <Button variant="outline" onClick={load} disabled={saving} data-testid="platform-cancel-btn">Reset</Button>
            <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold" onClick={save} disabled={saving} data-testid="platform-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save changes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
